import React, {useEffect, useMemo, useState, createRef} from 'react';
import {EquipmentHealthComponent} from './EquipmentHealthComponent';
import {useSiteHealth} from '../../providers/SiteHealthProvider';
import {Dimmer, Dropdown, Form, Loader} from 'semantic-ui-react';
import {useCustomers} from '../../providers/CustomerProvider';
import {useAuth} from "../../providers/AuthenticationProvider";
import {useNavigate} from "react-router";
import styles from "../DataInspector/DataInspector.module.css";
const WEB_PAGE = 'Site Health';

const timestyle = {
  display: 'flex',
  paddingLeft: '0.5rem',
  paddingRight: '0.5rem',
};

const roundTimeDownToQuarterHour = time => {
  let timeToReturn = new Date(time);

  timeToReturn.setMilliseconds(Math.floor(timeToReturn.getMilliseconds() / 1000) * 1000);
  timeToReturn.setSeconds(Math.floor(timeToReturn.getSeconds() / 60) * 60);
  timeToReturn.setMinutes(Math.floor(timeToReturn.getMinutes() / 15) * 15);
  return timeToReturn;
};

const hourArr = [...Array(12).keys()].map(i => {
  let time = i + 1;
  return {
    key: time,
    text: time,
    value: time,
  };
});
const minuteArr = [...Array(4).keys()].map(i => {
  let time = i * 15;
  return {
    key: time,
    text: time.toString().padStart(2, '0'),
    value: time,
  };
});

const createEquipmentRef = (equipment, refs) => {
  refs.current[equipment.value.id] = createRef();

  if (equipment.children.length > 0) {
    equipment.children.forEach(child => {
      createEquipmentRef(child, refs);
    });
  }
};

export const SiteHealth = () => {
  const { checkAuth, getUserType } = useAuth();
  const {results, err, isLoading, getSiteHealth, clearSiteHealth} = useSiteHealth();
  const {customersList, getAllCustomers} = useCustomers();
  const [selectedCustomer, setSelectedCustomer] = useState({value: null, text: 'Select Customer'});
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState({hours: 12, minutes: 0, tod: 'AM'});
  const [showAllEquipment, setShowAllEquipment] = useState(false);
  const equipmentRefs = React.useRef([]);
  const navigate = useNavigate();

  checkAuth(getUserType(), WEB_PAGE) && navigate('/');

  const statusDateTime = useMemo(() => {
    const nowUTC = new Date();
    const roundedDownNowUTC = roundTimeDownToQuarterHour(nowUTC.toUTCString());
    const statusDateTime = new Date(roundedDownNowUTC.toUTCString());
    statusDateTime.setMinutes(roundedDownNowUTC.getMinutes() - 30);
    return statusDateTime;
  }, []);

  useEffect(() => {
    if (results.length > 0) {
      results.forEach((equipment, index) => {
        createEquipmentRef(equipment, equipmentRefs);
      });
    }
  }, [results]);

  //sets the initial time for the form
  useEffect(() => {
    const hourlessUTC = new Date(statusDateTime);
    let adjustedTime = {
      hours: hourlessUTC.getHours() % 12 || 12,
      minutes: hourlessUTC.getMinutes(),
      tod: hourlessUTC.getHours() >= 12 ? 'PM' : 'AM',
    };
    setTime(adjustedTime);

    let dateUpdate = new Date(hourlessUTC.setHours(hourlessUTC.getHours(), adjustedTime.minutes, 0, 0));
    setDate(dateUpdate);
  }, [statusDateTime]);

  useEffect(() => {
    getAllCustomers();
  }, [getAllCustomers]);

  useEffect(() => {
    if (selectedCustomer && selectedCustomer.groupId)
      getSiteHealth(selectedCustomer.groupId, date.toISOString());
    else clearSiteHealth();
  }, [date, getSiteHealth, selectedCustomer, clearSiteHealth]);

  const handleTimeChange = t => {
    setTime(t);
    let hours = (t.tod === 'PM' && t.hours < 12) ? t.hours + 12 : t.hours;
    let dateUpdate = new Date(date.setHours(hours, t.minutes, 0, 0));
    setDate(dateUpdate);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <div className='pageHeader'>
          <h1>Site Health Dashboard</h1>
        </div>
      </div>
      <div
        style={{
          width: '800px',
          alignSelf: 'center',
        }}
      >
        <div
          style={{
            fontSize: '28px',
            color: '#153A4B',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Form>
            <div
              style={{
                display: 'flex',
                justifyContent: 'end',
                fontSize: '25px',
                margin: '15px 15px 15px 0px',
              }}
            >
              <div>
                <p style={{marginBottom: 0}}>Site Status as of:</p>
                <p style={{marginTop: 0}}>{date.toLocaleString()}</p>
              </div>
            </div>
            <Form.Group>
              <Form.Field
                  label={'Customer'}
                  control={Dropdown}
                  selection
                  options={customersList
                      .filter(x => x.groupId)
                      .map(x => {
                        const text =
                            x?.firstname && x?.lastname
                                ? `${x?.companyName} (${x?.firstname} ${x?.lastname})`
                                : x?.companyName;
                        return {
                          key: x?.id,
                          text: text,
                          value: x?.id,
                        };
                      })}
                  style={{height: '38px'}}
                  onChange={(e, selected) => {
                    const customer = customersList.find(x => x.id === selected.value);
                    setSelectedCustomer(customer);
                    getSiteHealth(customer.groupId, date.toISOString());
                  }}
                  width={12}
                  search
              />
              <Form.Input
                  type={'date'}
                  defaultValue={date.toLocaleDateString('en-CA')}
                  label="Date"
                  placeholder="Time Range"
                  onChange={e => {
                    //Html input returns day before selected, this fixes that
                    let newDate = new Date(e.target.value);
                    newDate.setDate(newDate.getDate() + 1);
                    setDate(newDate);
                  }}
              />
              <div style={timestyle}>
                <Form.Field
                    label={'Hour'}
                    control={Dropdown}
                    selection
                    compact
                    value={time.hours}
                    options={hourArr}
                    style={{height: '38px'}}
                    onChange={(e, selected) => {
                      handleTimeChange({...time, hours: selected.value});
                    }}
                />
                <Form.Field
                    label={'Min'}
                    control={Dropdown}
                    selection
                    compact
                    value={time.minutes}
                    options={minuteArr}
                    style={{height: '38px'}}
                    onChange={(e, selected) => {
                      handleTimeChange({...time, minutes: selected.value});
                    }}
                />
                <Form.Field
                    label={'Min'}
                    control={Dropdown}
                    selection
                    compact
                    value={time.tod}
                    options={[
                      {key: 'AM', text: 'AM', value: 'AM'},
                      {key: 'PM', text: 'PM', value: 'PM'},
                    ]}
                    style={{height: '38px'}}
                    onChange={(e, selected) => {
                      handleTimeChange({...time, tod: selected.value});
                    }}
                />
              </div>
              <div className={styles.lineToggle}>
                <span>Show All Equipment</span>
                <Form.Radio checked={showAllEquipment} toggle onChange={() => setShowAllEquipment(!showAllEquipment)}/>
              </div>
            </Form.Group>
          </Form>
        </div>
        <Dimmer active={isLoading} inverted>
          <Loader active={isLoading} size={'massive'}/>
        </Dimmer>
        {err && (
            <div>
              <pre>{JSON.stringify(err, null, 2)}</pre>
          </div>
        )}
        {(results ?? []).map(data => (
          <div key={data.value.id} ref={equipmentRefs.current[data.value.id]}>
            <EquipmentHealthComponent
              data={data}
              statusDateTime={date}
              key={data.value.id}
              equipmentRef={equipmentRefs}
              showAllEquipment={showAllEquipment}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
